<script setup lang="ts">
import type { Module } from '#shared/types'
import { ModuleProseA, ModuleProseKbd, ModuleProseImg, ModuleProsePicture } from '#components'

definePageMeta({
  heroBackground: 'opacity-30 -z-10'
})

const markdownComponents = {
  ...proseComponents,
  ProseA: ModuleProseA,
  ProseImg: ModuleProseImg,
  ProsePicture: ModuleProsePicture,
  ProseKbd: ModuleProseKbd
}

const route = useRoute()
const { isAgentDocked } = useNuxtAgent()

const { data: module } = await useFetch<Module>(`/api/v1/modules/${route.params.slug}`, { key: `module-${route.params.slug}` })
if (!module.value) {
  throw createError({ statusCode: 404, statusMessage: 'Module not found', fatal: true })
}

// Reassign the ref (not module.value.health = ...): useFetch `data` is a
// shallowRef, so an in-place nested write would not trigger reactivity.
const { health } = useModuleHealth()
watch(health, (map) => {
  const m = module.value
  if (m && map[m.name] && m.health !== map[m.name]) {
    module.value = { ...m, health: map[m.name] }
  }
})

const ownerName = computed(() => {
  const [owner, name] = module.value!.repo.split('#')[0].split('/')
  return `${owner}/${name}`
})

const links = computed(() => module.value
  ? [{
      icon: 'i-lucide-book',
      label: 'Documentation',
      to: `${module.value.website}?utm_source=nuxt.com&utm_medium=aside-module&utm_campaign=nuxt.com`,
      target: '_blank'
    }, {
      icon: 'i-simple-icons-github',
      label: ownerName.value,
      to: module.value.github,
      target: '_blank'
    }, module.value.npm && {
      icon: 'i-simple-icons-npm',
      label: module.value.npm,
      to: `https://npmjs.org/package/${module.value.npm}`,
      target: '_blank'
    }, module.value.learn_more && {
      icon: 'i-lucide-link',
      label: 'Learn more',
      to: module.value.learn_more,
      target: '_blank'
    }].filter(Boolean)
  : [])

const contributors = computed(() => {
  // A Nuxter profile when the contributor has one, GitHub otherwise (module repositories outside the Nuxt orgs).
  const allContributors = module.value.contributors.map(contributor => ({
    label: contributor.username,
    to: contributor.nuxter ? `/nuxters/${contributor.username}` : `https://github.com/${contributor.username}`,
    avatar: {
      provider: 'ipx',
      src: `https://ipx.nuxt.com/f_auto,s_20x20/gh_avatar/${contributor.username}`,
      srcset: `https://ipx.nuxt.com/f_auto,s_40x40/gh_avatar/${contributor.username} 2x`,
      alt: contributor.username
    }
  }))
  if (allContributors.length > 10) {
    return [...allContributors.slice(0, 10), {
      label: 'View all contributors',
      to: `https://github.com/${module.value.repo}/graphs/contributors`,
      external: true,
      target: '_blank',
      noAvatar: true
    }]
  }

  return allContributors
})

const title = module.value.npm
const description = module.value.description || 'A Nuxt module'
const publishedAgo = useTimeAgo(module.value.stats.publishedAt)
const createdAgo = useTimeAgo(module.value.stats.createdAt)

useSeoMeta({
  titleTemplate: '%s · Nuxt Modules',
  title,
  description
})
useCanonical()

if (import.meta.server) {
  useSeoMeta({
    ogDescription: description,
    ogTitle: `${title} · Nuxt Modules`
  })

  defineOgImage('Module.takumi', {
    icon: module.value?.icon,
    headline: 'Nuxt Modules',
    title,
    description
  })
}
</script>

<template>
  <UContainer v-if="module">
    <div v-if="!module.compatibility?.nuxt?.includes('^3') && !module.compatibility?.nuxt?.includes('>=3')" class="pt-8">
      <UAlert
        icon="i-lucide-triangle-alert"
        variant="subtle"
        title="This module is not yet compatible with Nuxt 3"
      >
        <template #description>
          Head over to <NuxtLink to="https://v2.nuxt.com" target="_blank" class="underline">
            v2.nuxt.com
          </NuxtLink>
        </template>
      </UAlert>
    </div>
    <UPageHeader
      :description="module.description"
      :ui="{
        headline: 'mb-8',
        wrapper: isAgentDocked
          ? 'flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between'
          : 'flex flex-col gap-4 lg:grid lg:grid-cols-12 lg:gap-10 lg:items-center',
        title: isAgentDocked ? '' : 'lg:col-span-9',
        links: isAgentDocked ? '' : 'lg:col-span-3 lg:justify-start lg:pl-6'
      }"
    >
      <template #headline>
        <UBreadcrumb :items="[{ label: 'Modules', to: '/modules' }, { to: { name: 'modules', query: { category: module.category } }, label: module.category }, { label: module.npm }]" />
      </template>
      <template #title>
        <div class="flex items-center gap-4">
          <UAvatar
            :src="moduleImage(module.icon)"
            :icon="moduleIcon(module.category)"
            :alt="module.name"
            size="xl"
            class="-m-1 rounded-none bg-transparent"
          />

          <div>
            {{ module.npm }}

            <UTooltip v-if="module.type === 'official'" text="Official module" class="tracking-normal">
              <UIcon name="i-lucide-medal" class="size-6 text-primary" />
            </UTooltip>
          </div>
        </div>
      </template>

      <template #links>
        <ModuleInstallGroup :module="module" class="hidden lg:flex" />
      </template>

      <div class="flex flex-col lg:flex-row lg:items-center gap-3 mt-4">
        <UTooltip text="Monthly NPM Downloads">
          <NuxtLink class="flex items-center gap-1.5" :to="`https://npmx.dev/package-stats/${module.npm}/v/${module.stats.version}?granularity=monthly`" target="_blank">
            <UIcon name="i-lucide-circle-arrow-down" class="size-5 shrink-0" />
            <span class="text-sm font-medium">{{ formatNumber(module.stats.downloads) }} downloads</span>
          </NuxtLink>
        </UTooltip>

        <span class="hidden lg:block text-muted">&bull;</span>

        <UTooltip text="GitHub Stars">
          <NuxtLink class="flex items-center gap-1.5" :to="`https://github.com/${(module.repo || '').split('#')[0]}`" target="_blank">
            <UIcon name="i-lucide-star" class="size-5 shrink-0" />
            <span class="text-sm font-medium">{{ formatNumber(module.stats.stars || 0) }} stars</span>
          </NuxtLink>
        </UTooltip>

        <span class="hidden lg:block text-muted">&bull;</span>

        <UTooltip text="Latest Version">
          <NuxtLink class="flex items-center gap-1.5" :to="`${(module.github || '').split('/tree/main/')[0]}/releases`" target="_blank">
            <UIcon name="i-lucide-tag" class="size-5 shrink-0" />
            <span class="text-sm font-medium">v{{ module.stats.version }}</span>
          </NuxtLink>
        </UTooltip>

        <template v-if="module.health">
          <span class="hidden lg:block text-muted">&bull;</span>
          <UTooltip :text="`Health: ${module.health.status} - ${module.health.score}/100`">
            <NuxtLink
              :to="`https://nuxt.care/?search=npm:${module.npm}`"
              class="flex items-center gap-1.5"
              target="_blank"
            >
              <UIcon name="i-lucide-heart-pulse" class="size-5 shrink-0" :style="{ color: module.health.color }" />
              <span class="text-sm font-medium">{{ module.health.score }}</span>
            </NuxtLink>
          </UTooltip>
        </template>

        <div class="mx-3 h-6 border-l border-gray-200 dark:border-gray-800 w-px hidden lg:block" />

        <div v-for="(maintainer, index) in module.maintainers" :key="maintainer.github" class="flex items-center gap-3">
          <NuxtLink
            :to="maintainer.nuxter ? `/nuxters/${maintainer.github}` : `https://github.com/${maintainer.github}`"
            :target="maintainer.nuxter ? undefined : '_blank'"
            class="flex items-center gap-1.5 hover:text-primary"
          >
            <UAvatar provider="ipx" :src="`https://ipx.nuxt.com/f_auto,s_20x20/gh_avatar/${maintainer.github}`" :srcset="`https://ipx.nuxt.com/f_auto,s_40x40/gh_avatar/${maintainer.github} 2x`" :alt="maintainer.github" size="xs" />
            <span class="text-sm font-medium">{{ maintainer.github }}</span>
          </NuxtLink>

          <span v-if="index < module.maintainers.length - 1" class="hidden lg:block text-muted">&bull;</span>
        </div>
      </div>

      <ModuleInstallGroup :module="module" class="w-full mt-6 lg:hidden" />
    </UPageHeader>

    <UPage
      :ui="isAgentDocked ? {
        center: 'lg:col-span-10',
        right: 'lg:hidden'
      } : { root: 'lg:grid-cols-12', center: 'lg:col-span-9', right: 'lg:col-span-3' }"
    >
      <UPageBody>
        <MarkdownDocument
          v-if="module.readme?.nodes?.length"
          :value="module.readme"
          :components="markdownComponents"
          class="first:[&_picture]:block first:[&_picture]:mb-4"
        />
      </UPageBody>

      <template #right>
        <UContentToc :links="module.readme?.meta?.toc?.links">
          <template #bottom>
            <div class="hidden lg:block space-y-6">
              <UPageLinks title="Links" :links="links" />

              <USeparator type="dashed" />

              <UPageLinks
                title="Details"
                :links="[
                  {
                    label: `Updated ${publishedAgo}`,
                    to: `https://github.com/${module.repo}`,
                    icon: 'i-lucide-radio'
                  },
                  {
                    label: `Created ${createdAgo}`,
                    to: `https://github.com/${module.repo}`,
                    icon: 'i-lucide-package'
                  }
                ]"
              />

              <UPageLinks :links="contributors">
                <template #title>
                  Contributors <UBadge :label="module.contributors.length.toString()" color="neutral" variant="subtle" size="sm" class="rounded-full" />
                </template>

                <template #link-leading="{ link }">
                  <UAvatar v-if="!(link as any).noAvatar" provider="ipx" :src="`https://ipx.nuxt.com/f_auto,s_20x20/gh_avatar/${link.label}`" :srcset="`https://ipx.nuxt.com/f_auto,s_40x40/gh_avatar/${link.label} 2x`" class="size-5" />
                </template>
              </UPageLinks>
              <USeparator type="dashed" />
              <SocialLinks />
            </div>
          </template>
        </UContentToc>
      </template>
    </UPage>
  </UContainer>
</template>
